/**
 * Único ponto de chamada REST.
 *
 * REST existe aqui só onde tRPC não serve: upload `multipart/form-data` e
 * download de arquivo. Todo o resto é tRPC.
 *
 * O sistema anterior tinha dez definições de `API_BASE` com três semânticas
 * diferentes e 26 `fetch` manuais espalhados pelos componentes.
 */

import type { FolhaDeImpressao } from "@foto/shared";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function extrairErro(res: Response): Promise<string> {
  try {
    const corpo = (await res.json()) as { error?: { message?: string } };
    return corpo.error?.message ?? `Falha na requisição (${res.status})`;
  } catch {
    return `Falha na requisição (${res.status})`;
  }
}

function interpretar(texto: string): unknown {
  try {
    return JSON.parse(texto) as unknown;
  } catch {
    return null;
  }
}

export interface ProgressoUpload {
  enviados: number;
  total: number;
  percent: number;
}

/**
 * Upload com progresso real.
 *
 * `fetch` não reporta progresso de envio, e numa rede de evento uma foto de
 * vários megabytes leva tempo suficiente para o operador achar que travou.
 * XHR ainda é a única forma de saber.
 *
 * `extras` são os campos de texto que viajam junto do arquivo (a biblioteca de figurinhas manda o `tipo` e
 * o `nome`). Entram **antes** do arquivo: o multer só entrega ao `fileFilter` os campos que já chegaram, e
 * um filtro que dependa do tipo precisa vê-lo primeiro.
 */
export function enviarArquivos<T>(
  caminho: string,
  campos: { nome: string; arquivos: File[] },
  aoProgredir?: (p: ProgressoUpload) => void,
  extras?: Record<string, string>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData();
    for (const [nome, valor] of Object.entries(extras ?? {})) form.append(nome, valor);
    for (const arquivo of campos.arquivos) form.append(campos.nome, arquivo);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", caminho);
    xhr.withCredentials = true;

    xhr.upload.addEventListener("progress", (e) => {
      if (!e.lengthComputable || !aoProgredir) return;
      aoProgredir({
        enviados: e.loaded,
        total: e.total,
        percent: Math.round((e.loaded / e.total) * 100),
      });
    });

    xhr.addEventListener("load", () => {
      const corpo = interpretar(xhr.responseText);

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(corpo as T);
        return;
      }

      const msg =
        (corpo as { error?: { message?: string } } | null)?.error?.message ??
        `Falha no envio (${xhr.status})`;
      reject(new ApiError(msg, xhr.status));
    });

    xhr.addEventListener("error", () => reject(new ApiError("Sem conexão com o servidor", 0)));
    xhr.addEventListener("abort", () => reject(new ApiError("Envio cancelado", 0)));

    xhr.send(form);
  });
}

/** Baixa por navegação direta — nunca `blob()`, que mata a aba no celular. */
export function baixar(caminho: string): void {
  window.location.href = caminho;
}

export async function pedirJson<T>(caminho: string, init?: RequestInit): Promise<T> {
  const res = await fetch(caminho, { ...init, credentials: "include" });
  if (!res.ok) throw new ApiError(await extrairErro(res), res.status);
  return res.json() as Promise<T>;
}

/**
 * Monta a folha de impressão no servidor e devolve o JPEG.
 *
 * `blob()` aqui, ao contrário de `baixar`: o ZIP de uma exportação pode ter
 * centenas de megabytes e mata a aba no celular; a folha é um JPEG de meio
 * megabyte, e a tela precisa dele em mãos para mostrar, baixar e compartilhar.
 */
export async function montarFolhaDeImpressao(pedido: FolhaDeImpressao): Promise<Blob> {
  const res = await fetch("/api/impressao/folha", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(pedido),
  });
  if (!res.ok) throw new ApiError(await extrairErro(res), res.status);
  return res.blob();
}

/**
 * A folha de marcadores já montada, para compartilhar com o app da Canon.
 *
 * `blob()` pelo mesmo motivo da folha de impressão: é um JPEG de meio megabyte, e o
 * compartilhamento nativo precisa do arquivo em mãos. Para só baixar, a navegação
 * direta (`baixar`) basta.
 */
export async function buscarFolhaDeMarcadores(id: number): Promise<Blob> {
  const res = await fetch(`/api/marcadores/${id}/folha`, { credentials: "include" });
  if (!res.ok) throw new ApiError(await extrairErro(res), res.status);
  return res.blob();
}
